import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import Joi from 'joi';

import { useCreateTunnelActionMutation, useGetQuestionsQuery } from 'api/main';
import {
    editingConditionSet,
    newActionConditionAdded,
    newActionConditionRemoved,
    newActionSet,
    newActionUpdated,
} from 'reducers/customTunnel';
import { customTunnelSchemas } from 'schemas';
import { customTunnelSelectors } from 'reducers';
import { ucFirst, validateSchema } from 'utils/helpers';
import { alertType, conditionType } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import ConditionItem from './ConditionItem';
import ScoringLimits from './ScoringLimits';
import {
    ActionButton,
    CancelButton,
    CheckboxLabel,
    CustomFormLabel,
    CustomSelect,
    CustomTextField,
    ErrorMessage,
    Loader,
} from 'components';
import { Box, MenuItem } from '@mui/material';

const alertLabels = {
    [alertType.WARNING]: ucFirst(texts.ALERTS.ORANGE),
    [alertType.CRITICAL]: ucFirst(texts.ALERTS.RED),
    [alertType.OK]: ucFirst(texts.ALERTS.GREEN),
};

const schema = {
    name: Joi.string().trim().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    questions: Joi.array().min(1).messages({ 'array.min': texts.ACTION_QUESTIONS_REQUIRED }),
    conditions: Joi.array().min(1).messages({ 'array.min': texts.ACTION_CONDITIONS_REQUIRED }),
    alertMessage: Joi.string()
        .trim()
        .required()
        .messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
};

const AddAction = ({ ppTunnelId, pmTunnelId }) => {
    const dispatch = useDispatch();
    const newAction = useSelector(customTunnelSelectors.getNewAction);
    const { data: ppTunnelQuestions = [], isLoading: ppQuestionsLoading } = useGetQuestionsQuery({
        tunnelId: ppTunnelId,
        pagination: false,
    });
    const { data: pmTunnelQuestions = [], isLoading: pmQuestionsLoading } = useGetQuestionsQuery({
        tunnelId: pmTunnelId,
        pagination: false,
    });
    const [createAction, { isLoading: createLoading, isSuccess: createSuccess }] =
        useCreateTunnelActionMutation();

    const [selectAll, setSelectAll] = useState(false);
    const [errors, setErrors] = useState({
        name: '',
        questions: '',
        conditions: '',
        alertMessage: '',
    });

    const canAddCondition = useMemo(
        () =>
            newAction.conditions.length !== 2 &&
            !newAction.conditions.find(c => c.type === conditionType.EQUAL),
        [newAction.conditions]
    );

    const handleSelectAllQuestions = useCallback(
        e => {
            const { checked } = e.target;
            setSelectAll(checked);
            setErrors(prevState => ({ ...prevState, questions: '' }));
            dispatch(
                newActionUpdated({ key: 'questions', value: checked ? [...ppTunnelQuestions] : [] })
            );
        },
        [dispatch, ppTunnelQuestions]
    );
    const handleSelectQuestion = useCallback(
        question => e => {
            const { checked } = e.target;
            if (checked) {
                dispatch(
                    newActionUpdated({
                        key: 'questions',
                        value: [...newAction.questions, question],
                    })
                );
            } else {
                dispatch(
                    newActionUpdated({
                        key: 'questions',
                        value: newAction.questions.filter(q => q.id !== question.id),
                    })
                );
            }
        },
        [dispatch, newAction.questions]
    );
    const handleChange = useCallback(
        key => e => {
            dispatch(newActionUpdated({ key, value: e.target.value }));
        },
        [dispatch]
    );
    const handleCheckbox = useCallback(
        e => dispatch(newActionUpdated({ key: 'wrongCharacter', value: e.target.checked })),
        [dispatch]
    );
    const handleAddCondition = useCallback(() => {
        // set local action form - the condition added to the list
        dispatch(newActionConditionAdded({ type: conditionType.LESS_THAN, limitValue: '' }));
        // set local condition form
        dispatch(editingConditionSet({ value: { type: conditionType.LESS_THAN, limitValue: '' } }));
    }, [dispatch]);
    const handleCleanupConditions = useCallback(() => {
        dispatch(
            newActionUpdated({
                key: 'conditions',
                value: newAction.conditions.filter(c => c.id || c.limitValue),
            })
        );
    }, [dispatch, newAction.conditions]);
    const handleCancel = useCallback(() => {
        dispatch(newActionSet(null));
    }, [dispatch]);

    const hanldeAddAction = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, newAction);
        if (valid) {
            const payload = customTunnelSchemas.createAction({
                newAction,
                ppTunnelId,
                pmTunnelId,
                ppTunnelQuestions,
                pmTunnelQuestions,
            });
            createAction(payload);
        } else {
            setErrors(newErrors);
        }
    }, [createAction, newAction, pmTunnelId, pmTunnelQuestions, ppTunnelId, ppTunnelQuestions]);

    const handleRemoveCondition = useCallback(
        index => () => {
            dispatch(newActionConditionRemoved(index));
        },
        [dispatch]
    );

    useEffect(() => {
        if (createSuccess) {
            // clear store data
            dispatch(newActionSet(null));
        }
    }, [createSuccess, dispatch]);

    return (
        <Box
            sx={theme => ({
                border: `3px solid ${theme.palette.primary.main}`,
                borderRadius: '5px',
                marginY: 1,
                padding: theme.spacing(2, 3),
            })}
        >
            <CustomTextField
                label={texts.ACTION_NAME}
                value={newAction.name}
                onChange={handleChange('name')}
                error={errors.name}
            />
            <CustomFormLabel>{texts.QUESTIONS}</CustomFormLabel>
            <CheckboxLabel
                id="select-all"
                label={texts.SELECT_ALL}
                checked={selectAll}
                sx={{ alignItems: 'center' }}
                onChange={handleSelectAllQuestions}
            />
            <Loader loading={ppQuestionsLoading || pmQuestionsLoading}>
                {ppTunnelQuestions.map(q => (
                    <CheckboxLabel
                        key={q.id}
                        label={q.title}
                        checked={!!newAction.questions.find(crt => crt.id === q.id)}
                        sx={{ width: '100%' }}
                        onChange={handleSelectQuestion(q)}
                    />
                ))}
            </Loader>
            <ErrorMessage message={errors.questions} />
            <ScoringLimits questions={newAction.questions} />
            <Box textAlign="center" mb={2}>
                <ActionButton
                    disabled={!canAddCondition}
                    variant="contained"
                    onClick={handleAddCondition}
                >
                    {texts.ADD_CONDITION}
                </ActionButton>
            </Box>
            <ErrorMessage message={errors.conditions} />
            {newAction.conditions.map((c, idx) => (
                <ConditionItem
                    key={`${c.type}-${c.limitValue}`}
                    condition={c}
                    index={idx}
                    onRemove={handleRemoveCondition(idx)}
                    formCleanup={handleCleanupConditions}
                />
            ))}
            <CustomSelect
                label={texts.ALERTS.ALERTS}
                value={newAction.alertType}
                onChange={handleChange('alertType')}
            >
                {Object.keys(alertType).map(key => (
                    <MenuItem key={key} value={alertType[key]}>
                        {alertLabels[alertType[key]]}
                    </MenuItem>
                ))}
            </CustomSelect>
            <CustomTextField
                label={texts.ALERTS.ALERT_TEXT}
                value={newAction.alertMessage}
                onChange={handleChange('alertMessage')}
                error={errors.alertMessage}
            />
            <CheckboxLabel
                label={texts.ALERTS.WRONG_CHARACTER}
                checked={newAction.wrongCharacter}
                onChange={handleCheckbox}
            />
            <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center">
                <ActionButton loading={createLoading} variant="contained" onClick={hanldeAddAction}>
                    {texts.SAVE}
                </ActionButton>
                <CancelButton disabled={createLoading} onClick={handleCancel}>
                    {texts.CANCEL}
                </CancelButton>
            </Box>
        </Box>
    );
};

AddAction.propTypes = {
    ppTunnelId: PropTypes.number,
    pmTunnelId: PropTypes.number,
};

export default AddAction;
